import React, { useState, useCallback, useEffect, useMemo, memo } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  Search,
  ExternalLink,
  FileText,
  Globe,
  Clock,
  BookOpen,
  Newspaper,
  Brain,
  Loader2,
  Image,
  File,
  Download,
  Bug, // Add Bug icon for debug section
  Copy, // Add Copy icon for debug section
  Eye, // For viewing content
  Zap, // For usage stats
  MessageSquare, // For follow-up questions
  Quote, // For citations
  Sparkles, // For enhanced content
} from 'lucide-react'
import { NodeProps, useReactFlow } from '@xyflow/react'
import { toast } from 'sonner'
import { useFileStore } from '@/stores/fileStore'
import MarkdownRenderer from '@/components/markdown-renderer/MarkdownRenderer'
import NodeBaseContainer from '@/components/nodes/base/base-container/NodeBaseContainer'

interface CategorizedResult {
  url: string
  title: string
  snippet?: string
  content?: string
  category: 'webpage' | 'file' | 'image'
  score?: number
  published_date?: string
  rawContent?: string | null
}

interface SearchImage {
  url: string
  title?: string
}

interface SearchCitation {
  url: string
  title?: string
}

interface SearchUsage {
  prompt_tokens?: number
  completion_tokens?: number
  total_tokens?: number
  num_sources_used?: number
}

interface SearchHistoryItem {
  id: string
  query: string
  searchEngine: string
  timestamp: number
  totalResults: number
  summary: string
  results: CategorizedResult[]
  searchParams: any
  images?: SearchImage[]
  citations?: SearchCitation[]
  usage?: SearchUsage
  followUpQuestions?: string[]
}

interface NodeWebSearchData {
  searchQuery?: string
  selectedPreset?: string
  searchPreset?: string
  customParams?: any
  useCustomParams?: boolean
  selectedResults?: number[]
  combineUrls?: boolean
  searchResults?: CategorizedResult[]
  searchHistory?: SearchHistoryItem[]
  selectedHistoryId?: string
  searchSummary?: string
  topSources?: string[]
  lastSearchTime?: number
  totalResults?: number
  command?: string
  extractUrls?: string[]
  downloadUrls?: string[]
  timestamp?: number
  searchParams?: any
  documentRequest?: {
    'sysagent-cmd'?: string
    'sysagent-response'?: string
    mode?: string
    processing?: boolean
    error?: string
    processedAt?: string
    message?: string
    query?: string
    params?: any
    historyId?: string
    selectedCategory?: 'webpage' | 'file' | 'image'
    selectedUrls?: string[]
    action?: 'open' | 'download'
    downloadUrls?: string[]
    downloadCategory?: string
  }
  statusMessage?: string
  // Additional fields from actual node data
  query?: string
  searchDepth?: string
  maxResults?: number
  includeDomains?: string
  excludeDomains?: string
  // Enhanced data fields for rich responses
  searchImages?: SearchImage[]
  searchCitations?: SearchCitation[]
  searchUsage?: SearchUsage
  followUpQuestions?: string[]
  searchMetadata?: {
    responseTime?: number
    totalSources?: number
    searchEngine?: string
    model?: string
    tokensUsed?: number
  }
  // Debug information
  debugInfo?: {
    rawResponse?: any
    searchEngine?: string
    responseTime?: number
    parsedResults?: number
    errorDetails?: string
    tavilyResponse?: any
    xaiResponse?: any
  }
  // NodeBaseContainer fields
  label?: string
  isLocked?: boolean
  isMinimized?: boolean
  minimizeLevel?: 'normal' | 'medium' | 'minimum'
  assignedEmails?: string[] | string
  dataSettings?: any
}

interface SearchPreset {
  name: string
  icon: React.ElementType
  description: string
  params: {
    search_engine?: 'tavily' | 'xai'
    search_depth?: 'basic' | 'advanced'
    max_results?: number
    include_answer?: boolean
    include_raw_content?: boolean
    include_images?: boolean
    include_domains?: string[]
    exclude_domains?: string[]
    topic?: string
  }
}

const searchPresets: SearchPreset[] = [
  {
    name: 'Normal Search',
    icon: Search,
    description: 'Balanced search with good coverage',
    params: {
      search_depth: 'basic',
      max_results: 10,
      include_answer: true,
      include_raw_content: false,
      include_images: true,
    },
  },
  {
    name: 'Quick Search',
    icon: Clock,
    description: 'Fast search with fewer results',
    params: {
      search_depth: 'basic',
      max_results: 5,
      include_answer: true,
      include_raw_content: false,
      include_images: true,
    },
  },
  {
    name: 'Deep Research',
    icon: BookOpen,
    description: 'Comprehensive search with detailed content',
    params: {
      search_depth: 'advanced',
      max_results: 20,
      include_answer: true,
      include_raw_content: true,
      include_images: true,
    },
  },  {
    name: 'Grok News Digest',
    icon: Newspaper,
    description: 'Live news digest using x.ai Grok',
    params: {
      search_engine: 'xai',
      search_depth: 'basic',
      max_results: 10,
      include_answer: true,
      include_raw_content: false,
      include_images: true,
      topic: 'news',
    },
  },
  {
    name: 'Grok Real-time',
    icon: Brain,
    description: 'Comprehensive real-time analysis using x.ai Grok',
    params: {
      search_engine: 'xai',
      search_depth: 'basic',
      max_results: 15,
      include_answer: true,
      include_raw_content: true,
      include_images: true,
    },
  },
]

const NodeWebSearch: React.FC<NodeProps & { data: NodeWebSearchData }> = ({ 
  id, 
  data,
  selected
}) => {
  const { updateNodeData } = useReactFlow()
  const { currentProjectId } = useFileStore()
    // NodeBaseContainer state
  const [isLocked, setIsLocked] = useState(!!data.isLocked)
  const [isMinimized, setIsMinimized] = useState(!!data.isMinimized)
  const [minimizeLevel, setMinimizeLevel] = useState<'normal' | 'medium' | 'minimum'>(
    data.minimizeLevel || 'normal'
  )
  const [assignedEmails, setAssignedEmails] = useState<string[]>(
    Array.isArray(data.assignedEmails) ? data.assignedEmails : 
    (data.assignedEmails ? data.assignedEmails.trim().split(/\s+/).filter(Boolean) : [])
  )
  
  // Local state (separated from collaborative data)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedPreset, setSelectedPreset] = useState('Normal Search')
  const [customParams, setCustomParams] = useState<any>({
    search_engine: 'tavily',
    search_depth: 'basic',
    max_results: 10,
    include_answer: true,
    include_raw_content: false,
    include_images: false
  })
  const [useCustomParams, setUseCustomParams] = useState(false)
  // New state for categorized results
  const [selectedWebpages, setSelectedWebpages] = useState<Set<string>>(new Set())
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set())
  const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set())
  
  const [isSearching, setIsSearching] = useState(false)
  // Get collaborative data
  const searchHistory = data.searchHistory || []
  const selectedHistoryId = data.selectedHistoryId
  const searchResults = data.searchResults || []
  const searchSummary = data.searchSummary || ''
  const isProcessing = data.documentRequest?.processing || false

  // Get the selected history item for additional data like images
  const selectedHistoryItem = selectedHistoryId ? searchHistory.find(h => h.id === selectedHistoryId) : null

  // Categorize results by type and include images from history
  const categorizedResults = useMemo(() => {
    const webpages = searchResults.filter(r => r.category === 'webpage')
    const files = searchResults.filter(r => r.category === 'file')
    let images = searchResults.filter(r => r.category === 'image')

    // Add images from the selected history item if available
    if (selectedHistoryItem?.images) {
      const historyImages = selectedHistoryItem.images.map(img => ({
        url: img.url,
        title: img.title || 'Image',
        category: 'image' as const,
        content: img.title || 'Image from search results',
        score: 1.0
      }))
      images = [...images, ...historyImages]
    }

    return {
      webpages,
      files,
      images
    }
  }, [searchResults, selectedHistoryItem])

  // Sync local state from collaborative data
  useEffect(() => {
    setSearchQuery(data.searchQuery || data.query || '')
  }, [data.searchQuery, data.query])

  useEffect(() => {
    setSelectedPreset(data.selectedPreset || 'Normal Search')
  }, [data.selectedPreset])

  useEffect(() => {
    setCustomParams(data.customParams || {
      search_engine: 'tavily',
      search_depth: 'basic',
      max_results: 10,
      include_answer: true,
      include_raw_content: false,
      include_images: false
    })
  }, [data.customParams])
  useEffect(() => {
    setUseCustomParams(data.useCustomParams || false)
  }, [data.useCustomParams])

  // Helper to sync to collaborative state
  const syncNodeData = useCallback((updates: Partial<NodeWebSearchData>) => {
    updateNodeData(id, { ...data, ...updates })
  }, [id, data, updateNodeData])

  // Handle download response from backend
  useEffect(() => {
    if (data.documentRequest?.['sysagent-response'] === 'WebSearch-Download-Ready' && 
        data.documentRequest?.downloadUrls && 
        data.documentRequest.downloadUrls.length > 0) {
      // Create NodeResource nodes for each download URL (like SmartNotes creates documents)
      const downloadUrls = data.documentRequest.downloadUrls
      const category = data.documentRequest.downloadCategory || 'webpage'
      
      downloadUrls.forEach((url, index) => {
        // Extract filename from URL
        const filename = url.split('/').pop() || `downloaded-${category}`
        // Guess content type from URL extension
        const contentType = url.toLowerCase().includes('.pdf') ? 'application/pdf' :
                           url.toLowerCase().includes('.jpg') || url.toLowerCase().includes('.jpeg') ? 'image/jpeg' :
                           url.toLowerCase().includes('.png') ? 'image/png' :
                           url.toLowerCase().includes('.gif') ? 'image/gif' :
                           'application/octet-stream'
        
        const nodeId = `resource-url-${Date.now()}-${index}`
        const event = new CustomEvent('create-resource-url-node', {
          detail: {
            nodeId,
            filename,
            url,
            contentType,
            isUrlResource: true,
            urlToDownload: url,
            downloadStatus: 'pending',
            downloadAttempted: false,
          },
        })
        document.dispatchEvent(event)
      })

      // Clear the download response to prevent re-triggering
      syncNodeData({
        documentRequest: {
          ...data.documentRequest,
          'sysagent-response': 'WebSearch-Download-Processed',
          downloadUrls: undefined,
          downloadCategory: undefined
        }
      })

      toast.success(`Created ${downloadUrls.length} Resource nodes for downloading`)
    }
  }, [data.documentRequest?.['sysagent-response'], data.documentRequest?.downloadUrls, syncNodeData])

  // NodeBaseContainer handlers
  const handleLock = () => {
    const newLockState = !isLocked
    setIsLocked(newLockState)
    syncNodeData({ isLocked: newLockState })
  }

  const handleMinimize = () => {
    const newMinState = !isMinimized
    setIsMinimized(newMinState)
    setMinimizeLevel(newMinState ? 'medium' : 'normal')
    syncNodeData({ 
      isMinimized: newMinState,
      minimizeLevel: newMinState ? 'medium' : 'normal'
    })
  }

  const handleMinimizeLevelChange = (level: 'normal' | 'medium' | 'minimum') => {
    setMinimizeLevel(level)
    setIsMinimized(level !== 'normal')
    syncNodeData({ 
      minimizeLevel: level,
      isMinimized: level !== 'normal'
    })
  }

  const handleTitleChange = (newTitle: string) => {
    syncNodeData({ label: newTitle })
  }

  const handleAssignUsersClick = () => {
    const emailsStr = window.prompt(
      'Enter emails separated by space',
      assignedEmails.join(' ')
    )
    if (emailsStr !== null) {
      const emailsArr = emailsStr.trim().split(/\s+/).filter(Boolean)
      setAssignedEmails(emailsArr)
      syncNodeData({ assignedEmails: emailsArr })
    }
  }
  // Custom buttons for NodeBaseContainer menu
  const customButtons = [
    // Data settings will be added later
  ]

  // Compute status for NodeBaseContainer
  const nodeStatus = useMemo(() => {
    if (isProcessing) return 'loading'
    if (data.statusMessage && data.statusMessage.toLowerCase().includes('error')) return 'error'
    if (searchResults.length > 0) return 'success'
    return 'initial'
  }, [isProcessing, data.statusMessage, searchResults.length])

  // Handle search execution
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a search query')
      return
    }

    setIsSearching(true)
    try {
      // Build search parameters
      const preset = searchPresets.find(p => p.name === selectedPreset)
      const searchParams = useCustomParams ? customParams : preset?.params || {}

      // Send documentRequest to processor (following collaborative pattern)
      syncNodeData({
        documentRequest: {
          'sysagent-cmd': 'WebSearch-Process-Started',
          query: searchQuery.trim(),
          params: searchParams,
          mode: 'search',
          processing: true
        },
        searchQuery: searchQuery.trim(),
        searchParams,
        timestamp: Date.now(),
      })

      toast.success('Search initiated')
    } catch (error) {
      console.error('Search error:', error)
      toast.error('Failed to initiate search')
    } finally {
      setIsSearching(false)
    }
  }
  // Handle loading search history item
  const handleLoadHistory = async (historyId: string) => {
    try {      syncNodeData({
        selectedHistoryId: historyId,
        timestamp: Date.now(),
      })

      const historyItem = searchHistory.find(h => h.id === historyId)
      if (historyItem) {
        syncNodeData({
          searchResults: historyItem.results,
          searchSummary: historyItem.summary,
          searchImages: historyItem.images || [],
          searchCitations: historyItem.citations || [],
          searchUsage: historyItem.usage,
          followUpQuestions: historyItem.followUpQuestions || [],
        })
      }

      toast.success('Loaded search history')
    } catch (error) {
      console.error('Load history error:', error)
      toast.error('Failed to load search history')
    }
  }

  // Handle categorized actions (open/download)
  const handleCategorizedAction = async (
    category: 'webpage' | 'file' | 'image',
    action: 'open' | 'download',
    selectedUrls: string[]
  ) => {
    if (selectedUrls.length === 0) {
      toast.error(`Please select ${category} items`)
      return
    }

    if (action === 'open') {
      // Open URLs in browser
      selectedUrls.forEach(url => {
        window.open(url, '_blank', 'noopener,noreferrer')
      })
      toast.success(`Opened ${selectedUrls.length} ${category} items`)
      return
    }    if (action === 'download') {
      if (!currentProjectId) {
        toast.error('No active project to download to')
        return
      }

      try {
        syncNodeData({
          documentRequest: {
            'sysagent-cmd': 'WebSearch-Process-Started',
            mode: 'action',
            action: 'download',
            selectedCategory: category,
            selectedUrls,
            processing: true
          },
          timestamp: Date.now(),
        })

        toast.success(`Downloading ${selectedUrls.length} ${category} items`)
      } catch (error) {
        console.error('Download error:', error)
        toast.error('Failed to initiate download')
      }
    }
  }

  // Handle extraction (create NodeWebExtract)
  const handleExtraction = (selectedUrls: string[]) => {
    if (selectedUrls.length === 0) {
      toast.error('Please select items to extract')
      return
    }

    // Create NodeWebExtract node for each URL (like SmartNotes creates documents)
    selectedUrls.forEach((url, index) => {
      const nodeId = `web-extract-${Date.now()}-${index}`
      const event = new CustomEvent('create-web-extract-node', {
        detail: {
          nodeId,
          url,
          extractionType: 'webpage',
          mode: 'normal'
        },
      })
      document.dispatchEvent(event)
    })

    toast.success(`Created ${selectedUrls.length} Web Extract nodes`)
  }

  // Helper functions for category selection
  const handleCategorySelection = (
    category: 'webpage' | 'file' | 'image',
    url: string,
    selected: boolean
  ) => {    const setters = {
      webpage: setSelectedWebpages,
      file: setSelectedFiles,
      image: setSelectedImages
    }
    
    const setter = setters[category]
    setter(prev => {
      const newSet = new Set(prev)
      if (selected) {
        newSet.add(url)
      } else {
        newSet.delete(url)
      }
      return newSet
    })
  }
  
  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString()
  }

  // Copy to clipboard function (similar to NodeResourcePDF)
  const copyToClipboard = (text: string, message: string) => {
    navigator.clipboard.writeText(text).then(
      () => {
        toast.success(message)
      },
      () => {
        toast.error('Failed to copy to clipboard')
      }
    )
  }

  // Copy debug information to clipboard
  const copyDebugInfo = () => {
    if (data.debugInfo) {
      const debugText = JSON.stringify(data.debugInfo, null, 2)
      copyToClipboard(debugText, 'Debug information copied to clipboard')
    }
  }
  return (
    <NodeBaseContainer
      id={id}
      data={data}
      resizable={!isLocked}
      minWidth={500}
      minHeight={400}
      isLocked={isLocked}
      isMinimized={isMinimized}
      minimizeLevel={minimizeLevel}
      nodeTitle={data.label || 'Web Search'}
      onLockChange={handleLock}
      onMinimizeChange={handleMinimize}
      onMinimizeLevelChange={handleMinimizeLevelChange}
      onTitleChange={handleTitleChange}      assignedEmails={assignedEmails}
      onAssignUsersClick={handleAssignUsersClick}
      status={nodeStatus}
      selected={selected}
      customButtons={customButtons}
    >
      {!isMinimized && (
        <div className="p-6 space-y-6">{/* Search Input Section */}
        {/* Header */}
        <div className="flex items-center gap-2">
          <Globe className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold">Web Search</h3>
          {searchResults.length > 0 && (
            <Badge variant="secondary">{searchResults.length} results</Badge>
          )}
        </div>

        {/* Search Query */}
        <div className="space-y-2">
          <Label htmlFor="search-query">Search Query</Label>
          <div className="flex gap-2">
            <Input
              id="search-query"
              placeholder="Enter your search query..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !isSearching && handleSearch()}
              className="flex-1"
            />
            <Button 
              onClick={handleSearch} 
              disabled={isSearching || !searchQuery.trim()}
              size="default"
            >
              {isSearching ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Search Presets */}
        <div className="space-y-3">
          <Label>Search Presets</Label>
          <div className="grid grid-cols-2 gap-2">
            {searchPresets.map((preset) => {
              const Icon = preset.icon
              return (
                <Button
                  key={preset.name}
                  variant={selectedPreset === preset.name ? "default" : "outline"}
                  size="sm"
                  className="justify-start h-auto p-3"
                  onClick={() => setSelectedPreset(preset.name)}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  <div className="text-left">
                    <div className="font-medium text-xs">{preset.name}</div>
                    <div className="text-xs text-muted-foreground">{preset.description}</div>
                  </div>
                </Button>
              )
            })}
          </div>
        </div>

        {/* Custom Parameters Toggle */}
        <div className="flex items-center justify-between">
          <Label htmlFor="custom-params" className="text-sm">
            Use Custom Parameters
          </Label>
          <Switch
            id="custom-params"
            checked={useCustomParams}
            onCheckedChange={setUseCustomParams}
          />
        </div>

        {/* Custom Parameters */}
        {useCustomParams && (
          <div className="space-y-3 p-3 border rounded-lg bg-muted/50">
            <Label className="text-sm font-medium">Custom Search Parameters</Label>
            <div className="grid grid-cols-2 gap-3">              <div className="space-y-2">
                <Label htmlFor="search-engine" className="text-xs">Search Engine</Label>
                <select
                  id="search-engine"
                  title="Select search engine"
                  value={customParams.search_engine || 'tavily'}
                  onChange={(e) => setCustomParams({ ...customParams, search_engine: e.target.value })}
                  className="w-full p-2 text-xs border rounded"
                >
                  <option value="tavily">Tavily</option>
                  <option value="xai">x.ai Grok</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="search-depth" className="text-xs">Search Depth</Label>
                <select
                  id="search-depth"
                  title="Select search depth"
                  value={customParams.search_depth || 'basic'}
                  onChange={(e) => setCustomParams({ ...customParams, search_depth: e.target.value })}
                  className="w-full p-2 text-xs border rounded"
                >
                  <option value="basic">Basic</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="max-results" className="text-xs">Max Results</Label>
                <Input
                  id="max-results"
                  type="number"
                  min="1"
                  max="50"
                  value={customParams.max_results || 10}
                  onChange={(e) => setCustomParams({ ...customParams, max_results: parseInt(e.target.value) })}
                  className="text-xs"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="include-answer"
                    checked={customParams.include_answer !== false}
                    onCheckedChange={(checked) => setCustomParams({ ...customParams, include_answer: checked })}
                  />
                  <Label htmlFor="include-answer" className="text-xs">Include Answer</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="include-raw"
                    checked={!!customParams.include_raw_content}
                    onCheckedChange={(checked) => setCustomParams({ ...customParams, include_raw_content: checked })}
                  />
                  <Label htmlFor="include-raw" className="text-xs">Include Raw Content</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="include-images"
                    checked={!!customParams.include_images}
                    onCheckedChange={(checked) => setCustomParams({ ...customParams, include_images: checked })}
                  />
                  <Label htmlFor="include-images" className="text-xs">Include Images</Label>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search Results History */}
        {searchHistory.length > 0 && (
          <div className="space-y-3">
            <Label>Search Results History</Label>
            <div className="max-h-40 overflow-y-auto space-y-2 border rounded-lg p-2">
              {searchHistory.map((historyItem) => (
                <div
                  key={historyItem.id}
                  className={`p-2 rounded cursor-pointer transition-colors ${
                    selectedHistoryId === historyItem.id 
                      ? 'bg-blue-100 border-blue-300' 
                      : 'bg-muted hover:bg-muted/80'
                  }`}
                  onClick={() => handleLoadHistory(historyItem.id)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium line-clamp-1">{historyItem.query}</div>
                      <div className="text-xs text-muted-foreground">
                        {formatDate(historyItem.timestamp)} • {historyItem.searchEngine} • {historyItem.totalResults} results
                      </div>
                    </div>
                    <Clock className="w-4 h-4 text-muted-foreground ml-2 flex-shrink-0" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Summary using MarkdownRenderer */}
        {searchSummary && (
          <div className="space-y-2">
            <Label>Search Summary</Label>
            <div className="border rounded-lg p-3 bg-muted/30">
              <MarkdownRenderer markdown={searchSummary} />
            </div>
          </div>
        )}

        {/* Categorized Results */}
        {searchResults.length > 0 && (
          <div className="space-y-4">
            <Label className="text-lg font-medium">Search Results</Label>

            {/* Webpages */}
            {categorizedResults.webpages.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    Webpages ({categorizedResults.webpages.length})
                  </Label>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleExtraction(Array.from(selectedWebpages))}
                      disabled={selectedWebpages.size === 0}
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      Extract ({selectedWebpages.size})
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleCategorizedAction('webpage', 'open', Array.from(selectedWebpages))}
                      disabled={selectedWebpages.size === 0}
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Open ({selectedWebpages.size})
                    </Button>
                  </div>
                </div>
                <div className="max-h-48 overflow-y-auto space-y-1 border rounded-lg p-2">
                  {categorizedResults.webpages.map((result, index) => (
                    <div key={index} className="flex items-start gap-2 p-2 hover:bg-muted/50 rounded">
                      <Checkbox
                        checked={selectedWebpages.has(result.url)}
                        onCheckedChange={(checked) => handleCategorySelection('webpage', result.url, checked as boolean)}
                        className="mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium line-clamp-1">{result.title}</div>
                        <div className="text-xs text-muted-foreground line-clamp-1">{result.url}</div>
                        <div className="text-xs text-muted-foreground line-clamp-2">{result.snippet}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Files */}
            {categorizedResults.files.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <File className="w-4 h-4" />
                    Files ({categorizedResults.files.length})
                  </Label>                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleExtraction(Array.from(selectedFiles))}
                      disabled={selectedFiles.size === 0}
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      Extract ({selectedFiles.size})
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleCategorizedAction('file', 'open', Array.from(selectedFiles))}
                      disabled={selectedFiles.size === 0}
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Open ({selectedFiles.size})
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleCategorizedAction('file', 'download', Array.from(selectedFiles))}
                      disabled={selectedFiles.size === 0 || !currentProjectId}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download ({selectedFiles.size})
                    </Button>
                  </div>
                </div>
                <div className="max-h-48 overflow-y-auto space-y-1 border rounded-lg p-2">
                  {categorizedResults.files.map((result, index) => (
                    <div key={index} className="flex items-start gap-2 p-2 hover:bg-muted/50 rounded">
                      <Checkbox
                        checked={selectedFiles.has(result.url)}
                        onCheckedChange={(checked) => handleCategorySelection('file', result.url, checked as boolean)}
                        className="mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium line-clamp-1">{result.title}</div>
                        <div className="text-xs text-muted-foreground line-clamp-1">{result.url}</div>
                        <div className="text-xs text-muted-foreground line-clamp-2">{result.snippet}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Images */}
            {categorizedResults.images.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    Images ({categorizedResults.images.length})
                  </Label>                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleExtraction(Array.from(selectedImages))}
                      disabled={selectedImages.size === 0}
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      Extract ({selectedImages.size})
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleCategorizedAction('image', 'open', Array.from(selectedImages))}
                      disabled={selectedImages.size === 0}
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Open ({selectedImages.size})
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleCategorizedAction('image', 'download', Array.from(selectedImages))}
                      disabled={selectedImages.size === 0 || !currentProjectId}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download ({selectedImages.size})
                    </Button>
                  </div>
                </div>
                <div className="max-h-48 overflow-y-auto space-y-1 border rounded-lg p-2">
                  {categorizedResults.images.map((result, index) => (
                    <div key={index} className="flex items-start gap-2 p-2 hover:bg-muted/50 rounded">
                      <Checkbox
                        checked={selectedImages.has(result.url)}
                        onCheckedChange={(checked) => handleCategorySelection('image', result.url, checked as boolean)}
                        className="mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium line-clamp-1">{result.title}</div>
                        <div className="text-xs text-muted-foreground line-clamp-1">{result.url}</div>
                        <div className="text-xs text-muted-foreground line-clamp-2">{result.snippet}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Enhanced Search Results Display */}
        {searchResults.length > 0 && (
          <div className="space-y-4">
            {/* Images Section */}
            {data.searchImages && data.searchImages.length > 0 && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Image className="w-4 h-4" />
                  Images ({data.searchImages.length})
                </Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto border rounded-lg p-2">
                  {data.searchImages.map((img, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={img.url}
                        alt={img.title || 'Search result image'}
                        className="w-full h-20 object-cover rounded border cursor-pointer hover:opacity-80"
                        onClick={() => window.open(img.url, '_blank')}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none'
                        }}
                      />
                      {img.title && (
                        <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-1 rounded-b truncate">
                          {img.title}
                        </div>
                      )}
                      <Button
                        size="icon"
                        variant="outline"
                        className="absolute top-1 right-1 h-6 w-6 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => window.open(img.url, '_blank')}
                      >
                        <ExternalLink className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Citations Section */}
            {data.searchCitations && data.searchCitations.length > 0 && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Quote className="w-4 h-4" />
                  Citations ({data.searchCitations.length})
                </Label>
                <div className="max-h-32 overflow-y-auto space-y-1 border rounded-lg p-2">
                  {data.searchCitations.map((citation, index) => (
                    <div key={index} className="flex items-center gap-2 p-1 hover:bg-muted/50 rounded">
                      <span className="text-xs font-mono bg-muted px-1 rounded">{index + 1}</span>
                      <a
                        href={citation.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 text-xs text-blue-600 hover:underline line-clamp-1"
                      >
                        {citation.title || citation.url}
                      </a>
                      <ExternalLink className="w-3 h-3 text-muted-foreground" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Follow-up Questions */}
            {data.followUpQuestions && data.followUpQuestions.length > 0 && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Follow-up Questions ({data.followUpQuestions.length})
                </Label>
                <div className="space-y-1 border rounded-lg p-2">
                  {data.followUpQuestions.map((question, index) => (
                    <div key={index} className="flex items-start gap-2 p-1 hover:bg-muted/50 rounded">
                      <span className="text-xs font-mono bg-muted px-1 rounded mt-1">{index + 1}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="flex-1 text-left justify-start h-auto p-1 text-xs"
                        onClick={() => {
                          setSearchQuery(question)
                          handleSearch()
                        }}
                      >
                        {question}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Usage Statistics */}
            {data.searchUsage && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Usage Statistics
                </Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs border rounded-lg p-2">
                  {data.searchUsage.prompt_tokens && (
                    <div className="bg-muted/30 p-2 rounded">
                      <div className="font-medium">Prompt Tokens</div>
                      <div className="text-muted-foreground">{data.searchUsage.prompt_tokens.toLocaleString()}</div>
                    </div>
                  )}
                  {data.searchUsage.completion_tokens && (
                    <div className="bg-muted/30 p-2 rounded">
                      <div className="font-medium">Completion Tokens</div>
                      <div className="text-muted-foreground">{data.searchUsage.completion_tokens.toLocaleString()}</div>
                    </div>
                  )}
                  {data.searchUsage.total_tokens && (
                    <div className="bg-muted/30 p-2 rounded">
                      <div className="font-medium">Total Tokens</div>
                      <div className="text-muted-foreground">{data.searchUsage.total_tokens.toLocaleString()}</div>
                    </div>
                  )}
                  {data.searchUsage.num_sources_used && (
                    <div className="bg-muted/30 p-2 rounded">
                      <div className="font-medium">Sources Used</div>
                      <div className="text-muted-foreground">{data.searchUsage.num_sources_used}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Search Metadata */}
            {data.searchMetadata && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Search Metadata
                </Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs border rounded-lg p-2">
                  {data.searchMetadata.searchEngine && (
                    <div className="bg-muted/30 p-2 rounded">
                      <div className="font-medium">Search Engine</div>
                      <div className="text-muted-foreground">{data.searchMetadata.searchEngine}</div>
                    </div>
                  )}
                  {data.searchMetadata.model && (
                    <div className="bg-muted/30 p-2 rounded">
                      <div className="font-medium">Model</div>
                      <div className="text-muted-foreground">{data.searchMetadata.model}</div>
                    </div>
                  )}
                  {data.searchMetadata.responseTime && (
                    <div className="bg-muted/30 p-2 rounded">
                      <div className="font-medium">Response Time</div>
                      <div className="text-muted-foreground">{data.searchMetadata.responseTime}ms</div>
                    </div>
                  )}
                  {data.searchMetadata.totalSources && (
                    <div className="bg-muted/30 p-2 rounded">
                      <div className="font-medium">Total Sources</div>
                      <div className="text-muted-foreground">{data.searchMetadata.totalSources}</div>
                    </div>
                  )}
                  {data.searchMetadata.tokensUsed && (
                    <div className="bg-muted/30 p-2 rounded">
                      <div className="font-medium">Tokens Used</div>
                      <div className="text-muted-foreground">{data.searchMetadata.tokensUsed.toLocaleString()}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Enhanced Result Details with Raw Content */}
            {searchResults.some(result => result.rawContent) && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  Detailed Content
                </Label>
                <div className="max-h-64 overflow-y-auto space-y-2 border rounded-lg p-2">
                  {searchResults.filter(result => result.rawContent).map((result, index) => (
                    <details key={index} className="border rounded p-2">
                      <summary className="cursor-pointer font-medium text-sm hover:text-blue-600">
                        {result.title}
                      </summary>
                      <div className="mt-2 text-xs">
                        <div className="text-muted-foreground mb-1">{result.url}</div>
                        {result.score && (
                          <div className="text-muted-foreground mb-2">Score: {(result.score * 100).toFixed(1)}%</div>
                        )}
                        <div className="bg-muted/20 p-2 rounded max-h-32 overflow-y-auto">
                          {result.rawContent}
                        </div>
                      </div>
                    </details>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Debug Information */}
        {data.debugInfo && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Bug className="w-4 h-4 text-orange-600" />
              <Label className="text-sm font-medium">Debug Information</Label>
            </div>
            <div className="bg-muted/20 border rounded-lg p-3 space-y-2">
              <div className="grid grid-cols-2 gap-4 text-xs">
                <div>
                  <span className="font-medium">Search Engine:</span> {data.debugInfo.searchEngine || 'Unknown'}
                </div>
                <div>
                  <span className="font-medium">Response Time:</span> {data.debugInfo.responseTime || 0}ms
                </div>
                <div>
                  <span className="font-medium">Parsed Results:</span> {data.debugInfo.parsedResults || 0}
                </div>
                {data.debugInfo.errorDetails && (
                  <div className="col-span-2">
                    <span className="font-medium text-red-600">Error:</span> {data.debugInfo.errorDetails}
                  </div>
                )}
              </div>
              {data.debugInfo.rawResponse && (
                <div className="relative">
                  {/* Copy button in top right corner */}
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={copyDebugInfo}
                    className="absolute top-2 right-2 z-10 h-6 w-6 p-1"
                    title="Copy debug info to clipboard"
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                  <details className="mt-2">
                    <summary className="text-xs font-medium cursor-pointer hover:text-blue-600">
                      Raw Response Data (Click to expand)
                    </summary>
                    <pre className="text-xs bg-slate-50 p-2 rounded mt-1 overflow-auto max-h-48 pr-8">
                      {JSON.stringify(data.debugInfo.rawResponse, null, 2)}
                    </pre>
                  </details>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Status message */}
        {data.statusMessage && (
          <div className="text-sm text-muted-foreground p-2 bg-muted/30 rounded">
            {data.statusMessage}
          </div>
        )}        {/* Processing indicator */}
        {isProcessing && (
          <div className="flex items-center gap-2 text-sm text-blue-600">
            <Loader2 className="w-4 h-4 animate-spin" />
            Processing search request...
          </div>
        )}
        </div>
      )}
    </NodeBaseContainer>
  )
}

export default memo(NodeWebSearch)
